'use client';

import { useTranslation } from 'react-i18next';
import { AVAILABLE_LOCALES } from '@/i18n';
import { selectLocale } from '@/services/locale-preference';

/**
 * EN | ES switch from the v8 header. Choosing a language remembers it and
 * writes it into the URL's `?lng=`, so a reload keeps it. The visible code
 * stays first in each accessible name ("EN English") so a voice user can say
 * what they see. A build that bundles no Spanish draws no switch.
 */
export function LanguageSwitch() {
  const { t, i18n } = useTranslation('signup');
  if (!AVAILABLE_LOCALES.includes('es')) return null;

  const isSpanish = i18n.language === 'es';
  const choose = (locale: 'en' | 'es') => {
    if (i18n.language === locale) return;
    selectLocale(i18n, locale).catch(() => {
      // The bundled copy cannot fail to load; a failure leaves the URL as it was.
    });
  };

  return (
    <div
      className="signup-language-switch"
      role="group"
      aria-label={t('languageSwitch.label')}
      data-testid="signup-language-switch"
    >
      <button
        type="button"
        lang="en"
        className="signup-language-switch__option"
        aria-current={isSpanish ? undefined : 'true'}
        onClick={() => choose('en')}
        data-analytics-action="site.language-toggle.activate"
        data-analytics-element-key="en"
        data-testid="signup-language-en"
      >
        {t('languageSwitch.en')}
        <span className="signup-visually-hidden"> {t('languageSwitch.enLabel')}</span>
      </button>
      <span className="signup-language-switch__divider" aria-hidden="true">
        {t('languageSwitch.divider')}
      </span>
      <button
        type="button"
        lang="es"
        className="signup-language-switch__option"
        aria-current={isSpanish ? 'true' : undefined}
        onClick={() => choose('es')}
        data-analytics-action="site.language-toggle.activate"
        data-analytics-element-key="es"
        data-testid="signup-language-es"
      >
        {t('languageSwitch.es')}
        <span className="signup-visually-hidden"> {t('languageSwitch.esLabel')}</span>
      </button>
    </div>
  );
}
